import {
  CANTON_FIX_SOURCE,
  CANTON_FIX_STATUS,
  formatParisDateTime,
  type CantonFixesOverview,
  type CantonFixListItem,
} from '@flux/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState, type JSX } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
  type DataTableEmptyState,
} from '@/components/data-table/data-table.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { fetchCantonFixes, fetchCantonFixesOverview, startCantonScan } from './api.js';
import { CantonFixDetailSheet } from './canton-fix-detail-sheet.js';
import {
  CantonFixFilters,
  CLEAR_CANTON_FIX_FILTERS,
  effectiveCantonStatus,
  hasPeriodFilter,
  readCantonFixFilters,
} from './canton-fix-filters.js';
import { CantonScanConfirmDialog, CantonScanStatusLine } from './canton-scan.js';
import { Dash } from './detail-parts.js';
import { useDetailSheetParam } from './detail-sheet.js';
import {
  effectiveFeatureMode,
  isRechargeTokenMissing,
  RECHARGE_NOT_CONFIGURED_EMPTY_STATE,
  RechargeModeBanner,
  useRechargeSummary,
} from './recharge-status.js';

const PAGE_SIZE = 50;
/** Rafraîchissement du suivi tant qu'une vérification tourne (`06` §8.2). */
const POLL_INTERVAL_MS = 2_000;
/** Triables côté serveur (`apps/server/src/http/api/recharge/canton-fixes.ts::SORT_FIELDS`). */
const SORTABLE_COLUMNS = ['updatedAt', 'zip', 'city', 'status'] as const;
const DEFAULT_SORT = 'updatedAt';
const SCAN_LABEL = 'Vérifier toutes les adresses';
const SCAN_REASON_ID = 'canton-scan-reason';

function isScanRunning(overview: CantonFixesOverview | undefined): boolean {
  return overview?.lastScan?.status === 'running';
}

const helper = createDataTableColumnHelper<CantonFixListItem>();

const columns = helper.columns([
  helper.accessor('updatedAt', {
    id: 'updatedAt',
    header: 'Date',
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatParisDateTime(new Date(info.getValue()))}
      </span>
    ),
  }),
  helper.display({
    id: 'address',
    header: 'Adresse',
    cell: ({ row }) => (
      <span className="flex min-w-0 flex-col">
        <span className="tabular-nums">{row.original.addressId}</span>
        {row.original.address1 === null ? null : (
          <span className="text-xs text-muted-foreground">{row.original.address1}</span>
        )}
      </span>
    ),
  }),
  helper.accessor('customerId', {
    id: 'customerId',
    header: 'Client',
    cell: (info) => {
      const value = info.getValue();
      return value === null ? <Dash /> : <span className="tabular-nums">{value}</span>;
    },
  }),
  helper.accessor('zip', {
    id: 'zip',
    header: 'NPA',
    cell: (info) => {
      const value = info.getValue();
      return value === null ? <Dash /> : <span className="tabular-nums">{value}</span>;
    },
  }),
  helper.accessor('city', {
    id: 'city',
    header: 'Ville',
    cell: (info) => info.getValue() ?? <Dash />,
  }),
  helper.accessor('province', {
    id: 'province',
    header: 'Canton écrit',
    cell: (info) => info.getValue() ?? <Dash />,
  }),
  helper.accessor('source', {
    id: 'source',
    header: 'Origine',
    cell: (info) => <StatusBadge registry={CANTON_FIX_SOURCE} code={info.getValue()} />,
  }),
  helper.accessor('status', {
    id: 'status',
    header: 'Statut',
    cell: (info) => <StatusBadge registry={CANTON_FIX_STATUS} code={info.getValue()} />,
  }),
]);

/**
 * Onglet Cantons suisses de l'écran Abonnements (`06` §3, §8.2) : corrections
 * de canton des adresses suisses (webhooks et vérifications), avec l'action
 * « Vérifier toutes les adresses ». Filtres, tri et page dans l'URL, détail
 * dans un panneau porté par `?id=`.
 */
export function CantonFixesTab(): JSX.Element {
  const rawSearch: unknown = useSearch({ strict: false });
  const queryClient = useQueryClient();
  const { page, sortBy, sortDir, setPage, setSort, setFilters } = useDataTableUrlState();
  const { id, openDetail, closeDetail } = useDetailSheetParam();
  const summary = useRechargeSummary();
  const tokenMissing = isRechargeTokenMissing(summary.data);
  const filters = readCantonFixFilters(rawSearch);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [simulation, setSimulation] = useState(true);
  /** Vérifications lancées depuis cet écran, en attente de leur toast de fin. */
  const startedHere = useRef(new Set<string>());
  /** Dernière vérification vue en cours : sa fin rafraîchit la liste. */
  const runningScanId = useRef<string | null>(null);

  const overview = useQuery({
    queryKey: queryKeys.subscriptions.cantonFixesOverview(),
    queryFn: fetchCantonFixesOverview,
    refetchInterval: (current) => (isScanRunning(current.state.data) ? POLL_INTERVAL_MS : false),
  });

  // Le défaut « À traiter » dépend de la synthèse : sans choix dans l'URL, on
  // attend sa réponse plutôt que d'afficher un filtre faux. En erreur, « Toutes ».
  const defaultKnown = filters.status !== undefined || !overview.isPending;
  const status = effectiveCantonStatus(filters, overview.data?.unresolvedCount ?? 0);

  const listParams = {
    page,
    pageSize: PAGE_SIZE,
    sortDir,
    ...(sortBy ? { sortBy } : {}),
    ...(status === 'all' ? {} : { status: [status] }),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.subscriptions.cantonFixes(listParams),
    queryFn: () => fetchCantonFixes(listParams),
    placeholderData: keepPreviousData,
    enabled: defaultKnown,
  });

  const scan = useMutation({
    mutationFn: startCantonScan,
    onSuccess: (started) => {
      startedHere.current.add(started.id);
      queryClient.setQueryData<CantonFixesOverview>(
        queryKeys.subscriptions.cantonFixesOverview(),
        (previous) => ({ unresolvedCount: previous?.unresolvedCount ?? 0, lastScan: started }),
      );
      setConfirmOpen(false);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.subscriptions.cantonFixesOverview(),
      });
    },
  });

  const lastScan = overview.data?.lastScan ?? null;

  useEffect(() => {
    if (lastScan === null) {
      return;
    }
    if (lastScan.status === 'running') {
      runningScanId.current = lastScan.id;
      return;
    }
    if (runningScanId.current === lastScan.id) {
      runningScanId.current = null;
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.cantonFixesAll });
    }
    if (startedHere.current.has(lastScan.id)) {
      startedHere.current.delete(lastScan.id);
      if (lastScan.status === 'succeeded') {
        toast.success('Vérification terminée');
      }
    }
  }, [lastScan, queryClient]);

  const running = isScanRunning(overview.data);
  const disabledReason = tokenMissing
    ? 'Ajoutez le token API dans Paramètres'
    : running
      ? 'Une vérification est en cours'
      : null;
  const canScan = disabledReason === null && !scan.isPending;
  const forcedSimulation = summary.data?.testMode === true;

  function openConfirm(): void {
    // Simulation cochée par défaut tant que la fonction n'est pas en Actif (`06` §3.3).
    const mode = summary.data ? effectiveFeatureMode(summary.data, 'swissCanton') : 'simulation';
    setSimulation(forcedSimulation || mode !== 'active');
    scan.reset();
    setConfirmOpen(true);
  }

  function resetFilters(): void {
    setFilters(CLEAR_CANTON_FIX_FILTERS);
  }

  const filteredEmpty: DataTableEmptyState = {
    title: 'Aucun résultat pour ces filtres',
    description: 'Élargissez la période ou choisissez un autre statut.',
    action: (
      <Button type="button" variant="outline" onClick={resetFilters}>
        Réinitialiser les filtres
      </Button>
    ),
  };
  const emptyState: DataTableEmptyState = hasPeriodFilter(filters) ||
    (status !== 'all' && status !== 'unresolved')
    ? filteredEmpty
    : tokenMissing
      ? RECHARGE_NOT_CONFIGURED_EMPTY_STATE
      : status === 'unresolved'
        ? {
            title: 'Aucune adresse à traiter',
            description: 'Toutes les adresses suisses ont un canton.',
          }
        : {
            title: 'Aucune correction',
            description: 'Les adresses suisses sans canton seront complétées ici automatiquement.',
          };

  return (
    <>
      <RechargeModeBanner feature="swissCanton" />
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Canton complété à partir du NPA, sans jamais deviner
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {disabledReason === null ? null : (
              <p id={SCAN_REASON_ID} className="text-sm text-muted-foreground">
                {disabledReason}
              </p>
            )}
            <Button
              type="button"
              disabled={!canScan}
              isLoading={running && !tokenMissing}
              aria-describedby={disabledReason === null ? undefined : SCAN_REASON_ID}
              onClick={openConfirm}
            >
              {SCAN_LABEL}
            </Button>
          </div>
        </div>
        <CantonScanStatusLine
          scan={lastScan}
          canRetry={canScan}
          onRetry={openConfirm}
          loadError={
            overview.isError
              ? overview.error instanceof ApiError
                ? overview.error.message
                : 'Impossible de lire la dernière vérification.'
              : null
          }
          onReload={() => {
            void overview.refetch();
          }}
        />
      </div>
      <DataTable
        caption="Corrections de canton des adresses suisses"
        columns={columns}
        data={query.data?.items ?? []}
        getRowId={(row) => row.id}
        total={query.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        sortBy={sortBy ?? DEFAULT_SORT}
        sortDir={sortDir}
        sortableColumnIds={SORTABLE_COLUMNS}
        onPageChange={setPage}
        onSortChange={setSort}
        onRowClick={(row) => {
          openDetail(row.id);
        }}
        isLoading={!defaultKnown || query.isPending}
        isError={query.isError}
        errorMessage={
          query.error instanceof ApiError
            ? query.error.message
            : 'Impossible de charger les corrections de canton.'
        }
        errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        toolbar={<CantonFixFilters value={filters} status={status} onChange={setFilters} />}
        emptyState={emptyState}
      />
      <CantonScanConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        simulation={forcedSimulation || simulation}
        onSimulationChange={setSimulation}
        forcedSimulation={forcedSimulation}
        isStarting={scan.isPending}
        error={
          scan.error instanceof ApiError
            ? scan.error.message
            : scan.error
              ? 'Impossible de lancer la vérification. Réessayez.'
              : null
        }
        onConfirm={() => {
          scan.mutate({ simulation: forcedSimulation || simulation });
        }}
      />
      <CantonFixDetailSheet id={id} onClose={closeDetail} />
    </>
  );
}
